import { useState, useEffect } from 'react';
import { supabase, toError } from './supabase';
import { getPaperVenueLabel } from './sourceHelpers';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from './i18n/messages';

export interface PublicDigestPaper {
    id: string;
    short_id: string | null;
    slug: string | null;
    paper_title?: string | null;
    title: string;
    source: string | null;
}

export interface PublicDigestData {
    projectId: string;
    projectName: string;
    digestId: string;
    digestShortId: string | null; // Short ID (first 8 chars of UUID)
    digestDate: string;
    categories: string;
    researcherName: string | null;
    papers: PublicDigestPaper[];
    userId?: string; // Optional - only included when needed for ownership check
}

interface UsePublicHomeDigestsReturn {
    digests: PublicDigestData[] | null;
    isLoading: boolean;
    error: LocalizedMessage | null;
    targetCount: number;
}

/**
 * Hook to fetch public digests for display on the home page
 * Reads from environment variables (inlined at build time by Metro):
 * - EXPO_PUBLIC_HOME_PAGE_PUBLIC_PROJECTS: comma-separated list of project UUIDs
 * - EXPO_PUBLIC_HOME_PAGE_RESEARCHERS: comma-separated list of researcher names (maps 1:1 with projects)
 * - EXPO_PUBLIC_HOME_PAGE_NUM_PUBLIC_DIGESTS: number of digests to show (default: 3)
 */
export function usePublicHomeDigests(): UsePublicHomeDigestsReturn {
    const [digests, setDigests] = useState<PublicDigestData[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const [targetCount, setTargetCount] = useState(3);

    useEffect(() => {
        const fetchPublicDigests = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Read environment variables (Metro inlines these at build time)
                const projectIdsStr: string | undefined = process.env.EXPO_PUBLIC_HOME_PAGE_PUBLIC_PROJECTS;
                const researchersStr: string | undefined = process.env.EXPO_PUBLIC_HOME_PAGE_RESEARCHERS;
                const numDigestsStr = process.env.EXPO_PUBLIC_HOME_PAGE_NUM_PUBLIC_DIGESTS;
                const numDigests = parseInt(numDigestsStr || '3', 10);

                // Set target count
                setTargetCount(numDigests);

                // If no project IDs configured, return empty array
                if (!projectIdsStr || projectIdsStr.trim() === '') {
                    console.log('📰 [usePublicHomeDigests] No project IDs configured');
                    setDigests([]);
                    setIsLoading(false);
                    return;
                }

                // Parse project IDs
                const projectIds = projectIdsStr
                    .split(',')
                    .map(id => id.trim())
                    .filter(id => id.length > 0);

                // Parse researcher names (same order as project IDs)
                const researchers = researchersStr
                    ? researchersStr.split(',').map(name => name.trim())
                    : [];

                if (projectIds.length === 0) {
                    console.log('📰 [usePublicHomeDigests] No valid project IDs found');
                    setDigests([]);
                    setIsLoading(false);
                    return;
                }

                console.log('📰 [usePublicHomeDigests] Fetching digests for projects:', projectIds);

                // Fetch projects with categories
                const { data: projects, error: projectsError } = await supabase
                    .from('project')
                    .select(`
                        id,
                        name,
                        visibility,
                        project_category_interests (
                            category (
                                id,
                                name,
                                code
                            )
                        )
                    `)
                    .in('id', projectIds)
                    .eq('visibility', 'public')
                    .is('deleted_at', null);

                if (projectsError) {
                    console.error('📰 [usePublicHomeDigests] Error fetching projects:', projectsError);
                    throw toError(projectsError);
                }

                if (!projects || projects.length === 0) {
                    console.log('📰 [usePublicHomeDigests] No public projects found');
                    setDigests([]);
                    setIsLoading(false);
                    return;
                }

                console.log('📰 [usePublicHomeDigests] Found public projects:', projects.length);

                // For each project, fetch the most recent sent digest
                const digestPromises = projects.map(async (project) => {
                    try {
                        // Find the index of this project in the original projectIds array
                        const projectIndex = projectIds.indexOf(project.id);
                        const researcherName = projectIndex >= 0 && projectIndex < researchers.length
                            ? researchers[projectIndex]
                            : null;
                        // Fetch last 5 sent digests
                        const { data: recentDigests, error: digestError } = await supabase
                            .from('email_digest')
                            .select('id, short_id, created_at, sent_at')
                            .eq('project_id', project.id)
                            .eq('status', 'sent')
                            .order('created_at', { ascending: false })
                            .limit(5);

                        if (digestError || !recentDigests || recentDigests.length === 0) {
                            console.log(`📰 [usePublicHomeDigests] No sent digest for project ${project.id}`);
                            return null;
                        }

                        // Accumulate papers from digests until we have 5
                        const papers: PublicDigestPaper[] = [];
                        for (const digest of recentDigests) {
                            if (papers.length >= 5) break;

                            const { data: digestPapers, error: papersError } = await supabase
                                .from('digest_papers')
                                .select(`
                                    rank_position,
                                    project_paper:project_paper_id (
                                        id,
                                        short_id,
                                        slug,
                                        paper_title,
                                        paper:paper_id (
                                            id,
                                            title,
                                            source_id,
                                            source_paper_id,
                                            doi,
                                            url
                                        )
                                    )
                                `)
                                .eq('digest_id', digest.id)
                                .order('rank_position', { ascending: true })
                                .limit(5 - papers.length);

                            if (papersError || !digestPapers) {
                                console.log(`📰 [usePublicHomeDigests] No papers for digest ${digest.id}`);
                                continue;
                            }

                            for (const dp of digestPapers) {
                                if (papers.length >= 5) break;
                                const projectPaper = (dp as any).project_paper;
                                const paper = projectPaper?.paper;
                                if (!paper) continue;

                                papers.push({
                                    id: projectPaper.id, // Use project_paper.id for linking
                                    short_id: projectPaper.short_id,
                                    slug: projectPaper.slug,
                                    paper_title: projectPaper.paper_title,
                                    title: paper.title,
                                    source: getPaperVenueLabel({
                                        sourceId: paper.source_id,
                                        sourcePaperId: paper.source_paper_id,
                                        doi: paper.doi,
                                        url: paper.url,
                                    }),
                                });
                            }
                        }

                        if (papers.length < 3) {
                            console.log(`📰 [usePublicHomeDigests] Not enough papers for project ${project.id} (found ${papers.length})`);
                            return null;
                        }

                        // Extract categories
                        const categories = project.project_category_interests
                            ?.map((pci: any) => pci.category?.name || pci.category?.code)
                            .filter(Boolean)
                            .slice(0, 3) // Limit to 3 categories
                            .join(', ') || 'Research';

                        // Use most recent digest for metadata
                        const mostRecentDigest = recentDigests[0];

                        return {
                            projectId: project.id,
                            projectName: project.name,
                            digestId: mostRecentDigest.id,
                            digestShortId: mostRecentDigest.short_id,
                            digestDate: mostRecentDigest.sent_at || mostRecentDigest.created_at,
                            categories,
                            researcherName,
                            papers
                        };
                    } catch (err) {
                        console.error(`📰 [usePublicHomeDigests] Error processing project ${project.id}:`, err);
                        return null;
                    }
                });

                const results = await Promise.all(digestPromises);

                // Filter out null results and limit to numDigests
                const validDigests = results
                    .filter((d): d is PublicDigestData => d !== null)
                    .slice(0, numDigests);

                console.log('📰 [usePublicHomeDigests] Successfully fetched digests:', validDigests.length);
                setDigests(validDigests);
            } catch (err: any) {
                console.error('📰 [usePublicHomeDigests] Unexpected error:', err);
                setError(msg({ id: 'publicDigests.loadFailure', message: 'Failed to load public digests' }));
                setDigests([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPublicDigests();
    }, []); // Run once on mount

    return {
        digests,
        isLoading,
        error,
        targetCount
    };
}
